"use client";

/**
 * The no-login customer chat link.
 *
 * SECURE_LINKS: this url goes to an EXTERNAL party and opens ONLY this project's
 * chat, which is why regenerating is a separate, explicitly-labelled action —
 * it revokes the link anyone already has.
 */

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import type { DealProjectChannelState } from "./useDealProjectChannel";

export function CustomerChatLinkCard({ state }: { state: DealProjectChannelState }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Customer chat link (no login)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Share this link so the customer can chat about this project without signing in. It opens
          ONLY this project&apos;s chat.
        </p>
        {state.linkError ? (
          /* The read failed, so we do not know whether a link already exists.
             Minting one blind would hand the customer a second url for the same
             project (SECURE_LINKS), so offer the retry instead of the button. */
          <div
            role="alert"
            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>{state.linkError}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={state.retryLink}
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        ) : state.linkUrl ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={state.linkUrl}
                className="w-full rounded-lg border bg-muted/40 p-2 text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button type="button" variant="outline" onClick={state.copyLink}>
                {state.copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => state.generateLink(true)}
              disabled={state.linkLoading}
            >
              {state.linkLoading ? "Working…" : "Regenerate (revokes old)"}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            onClick={() => state.generateLink(false)}
            disabled={state.linkLoading}
          >
            {state.linkLoading ? "Generating…" : "Generate chat link"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
